/**
 * UI/UX's tools in the style bar (where UI/UX is worked in): a frame's device
 * (`#board-device`), auto layout (`#board-layout`, `#board-gap`, `#board-padding`,
 * `#board-align-items`) and "Component"; an element's corners (`#board-radius`), an input's
 * hidden words and a checkbox's tick.
 */
import { For, Show, type JSX } from "solid-js";
import { useApp, usePack } from "../../../app/context";
import type { Bar } from "../../../state/board";
import { DEVICES, SPACING_MAX } from "../constants";
import type { UiUx } from "../service";

/** The corners offered (CSS pixels; {@link ROUND} is round). */
const RADII = ["0", "4", "8", "12", "24", "999"];

/** The corner radius meaning "round". */
const ROUND = "999";

/** A number field of a layout frame (`gap`, `padding`): `0` unsets it. */
function Spacing(props: { bar: Bar; key: "gap" | "padding" }): JSX.Element {
  const { board } = useApp();
  return (
    <label class="board-field">
      {props.key}
      <input
        id={`board-${props.key}`}
        type="number"
        min="0"
        max={SPACING_MAX}
        value={props.bar.value(props.key) || "0"}
        onChange={(e) => void board.setStyle(props.key, e.currentTarget.value === "0" ? "" : e.currentTarget.value)}
      />
    </label>
  );
}

/** A frame's device, auto layout and component switch. */
export function ScreenTools(props: { bar: Bar }): JSX.Element {
  const { board } = useApp();
  const uiux = usePack<UiUx>("uiux");
  const { value } = props.bar;
  return (
    <>
      <select id="board-device" aria-label="Device" value={value("device")} onChange={(e) => void uiux.setDevice(e.currentTarget.value)}>
        <option value="">free size</option>
        <For each={Object.entries(DEVICES)}>{([id, d]) => <option value={id}>{`${d.label} ${d.w}×${d.h}`}</option>}</For>
      </select>
      <select id="board-layout" aria-label="Auto layout" value={value("layout")} onChange={(e) => void board.setStyle("layout", e.currentTarget.value)}>
        <option value="">no auto layout</option>
        <option value="column">column ↓</option>
        <option value="row">row →</option>
      </select>
      <Show when={value("layout")}>
        <Spacing bar={props.bar} key="gap" />
        <Spacing bar={props.bar} key="padding" />
        <select id="board-align-items" aria-label="Line up across" value={value("align") || "start"} onChange={(e) => void board.setStyle("align", e.currentTarget.value === "start" ? "" : e.currentTarget.value)}>
          <option value="start">start</option>
          <option value="center">centre</option>
          <option value="end">end</option>
          <option value="stretch">stretch</option>
        </select>
      </Show>
      <button type="button" data-board-action="component" aria-pressed={value("component") === "yes"} title="A component: its instances follow every change made to it" onClick={() => void uiux.toggleComponent()}>
        ◇ Component
      </button>
    </>
  );
}

/** The elements drawn without corners of their own: words, notes, lists and checkboxes. */
const NO_CORNERS = new Set(["text", "note", "list", "checkbox"]);

/** An element's corners, an input's hidden words, a checkbox's tick. */
export function ElementTools(props: { bar: Bar }): JSX.Element {
  const { board } = useApp();
  const { shape, value, set } = props.bar;
  return (
    <>
      <Show when={!NO_CORNERS.has(shape())}>
        <select id="board-radius" aria-label="Corners" value={value("radius")} onChange={(e) => void board.setStyle("radius", e.currentTarget.value)}>
          <option value="">usual corners</option>
          <For each={RADII}>{(r) => <option value={r}>{r === ROUND ? "round" : `${r} px corners`}</option>}</For>
        </select>
      </Show>
      <Show when={shape() === "input"}>
        <button type="button" data-style="secret" data-value="yes" aria-pressed={value("secret") === "yes"} title="Hide what is typed (a password)" onClick={() => set("secret", "yes")}>
          ••• hidden
        </button>
      </Show>
      <Show when={shape() === "checkbox"}>
        <button type="button" data-style="checked" data-value="yes" aria-pressed={value("checked") === "yes"} title="Ticked at first" onClick={() => set("checked", "yes")}>
          ☑ ticked
        </button>
      </Show>
    </>
  );
}
